import { supabase } from "@/infra/supabase/client";

export type AccessStatusKind = "active" | "expiring" | "expired" | "lifetime";

export type AccessStatus = {
  expiresAt: Date | null;
  isLifetime: boolean;
  daysRemaining: number | null;
  status: AccessStatusKind;
};

export type RenewalType =
  | "preset_3m"
  | "preset_6m"
  | "preset_12m"
  | "custom"
  | "lifetime";

export type Renewal = {
  id: string;
  userId: string;
  renewedBy: { id: string; fullName: string | null };
  previousExpiresAt: Date | null;
  newExpiresAt: Date | null;
  renewalType: RenewalType;
  reason: string | null;
  createdAt: Date;
};

export type RenewalInput =
  | { type: "preset"; months: 3 | 6 | 12; reason?: string }
  | { type: "custom"; expiresAt: Date; reason?: string }
  | { type: "lifetime"; reason?: string };

const EXPIRING_THRESHOLD_DAYS = 30;

export function computeAccessStatus(
  expiresAt: Date | null,
  isAdmin: boolean,
  now: Date = new Date(),
): AccessStatus {
  if (isAdmin || expiresAt === null) {
    return {
      expiresAt: null,
      isLifetime: true,
      daysRemaining: null,
      status: "lifetime",
    };
  }
  const msPerDay = 1000 * 60 * 60 * 24;
  const diff = Math.ceil((expiresAt.getTime() - now.getTime()) / msPerDay);
  let status: AccessStatusKind;
  if (diff < 0) status = "expired";
  else if (diff <= EXPIRING_THRESHOLD_DAYS) status = "expiring";
  else status = "active";
  return {
    expiresAt,
    isLifetime: false,
    daysRemaining: diff,
    status,
  };
}

export function computeNewExpiresAt(
  input: RenewalInput,
  currentExpiresAt: Date | null,
  now: Date = new Date(),
): Date | null {
  if (input.type === "lifetime") return null;
  if (input.type === "custom") return input.expiresAt;
  // preset: base é o maior entre now e current (renovar expirado parte de hoje)
  const base =
    currentExpiresAt && currentExpiresAt > now ? currentExpiresAt : now;
  const result = new Date(base);
  result.setMonth(result.getMonth() + input.months);
  return result;
}

export function renewalTypeFromInput(input: RenewalInput): RenewalType {
  if (input.type === "lifetime") return "lifetime";
  if (input.type === "custom") return "custom";
  return `preset_${input.months}m` as RenewalType;
}

export const userAccessService = {
  async getAccessStatus(userId: string): Promise<AccessStatus> {
    const { data, error } = await supabase
      .from("profiles")
      .select("expires_at, is_admin")
      .eq("id", userId)
      .maybeSingle();
    if (error) throw error;
    if (!data) throw new Error(`Profile not found: ${userId}`);
    const expiresAt = data.expires_at ? new Date(data.expires_at) : null;
    return computeAccessStatus(expiresAt, !!data.is_admin);
  },

  async renewAccess(
    userId: string,
    input: RenewalInput,
  ): Promise<AccessStatus> {
    const { data: profile, error: fetchErr } = await supabase
      .from("profiles")
      .select("expires_at, is_admin")
      .eq("id", userId)
      .maybeSingle();
    if (fetchErr) throw fetchErr;
    if (!profile) throw new Error(`Profile not found: ${userId}`);

    const previousExpiresAt = profile.expires_at
      ? new Date(profile.expires_at)
      : null;
    const newExpiresAt = computeNewExpiresAt(input, previousExpiresAt);

    const { error: updateErr } = await supabase
      .from("profiles")
      .update({ expires_at: newExpiresAt ? newExpiresAt.toISOString() : null })
      .eq("id", userId);
    if (updateErr) throw updateErr;

    const {
      data: { user: authUser },
    } = await supabase.auth.getUser();
    if (!authUser) throw new Error("Not authenticated");

    const { error: auditErr } = await supabase.from("access_renewals").insert({
      user_id: userId,
      renewed_by: authUser.id,
      previous_expires_at: previousExpiresAt
        ? previousExpiresAt.toISOString()
        : null,
      new_expires_at: newExpiresAt ? newExpiresAt.toISOString() : null,
      renewal_type: renewalTypeFromInput(input),
      reason: ("reason" in input ? input.reason : null) ?? null,
    });
    if (auditErr) throw auditErr;

    return computeAccessStatus(newExpiresAt, !!profile.is_admin);
  },

  async getRenewalHistory(
    userId: string,
    limit: number = 20,
  ): Promise<Renewal[]> {
    const { data, error } = await supabase
      .from("access_renewals")
      .select(
        `
        id,
        user_id,
        previous_expires_at,
        new_expires_at,
        renewal_type,
        reason,
        created_at,
        renewed_by_profile:profiles!access_renewals_renewed_by_fkey (id, full_name)
      `,
      )
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(limit);
    if (error) throw error;
    return (data ?? []).map(
      (row): Renewal => ({
        id: row.id,
        userId: row.user_id,
        renewedBy: {
          id: (row.renewed_by_profile as { id: string }).id,
          fullName: (row.renewed_by_profile as { full_name: string | null })
            .full_name,
        },
        previousExpiresAt: row.previous_expires_at
          ? new Date(row.previous_expires_at)
          : null,
        newExpiresAt: row.new_expires_at ? new Date(row.new_expires_at) : null,
        renewalType: row.renewal_type as RenewalType,
        reason: row.reason,
        createdAt: new Date(row.created_at),
      }),
    );
  },

  async listExpiringSoon(daysAhead: number = 30): Promise<
    Array<{
      id: string;
      fullName: string | null;
      email: string | null;
      expiresAt: Date;
    }>
  > {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() + daysAhead);
    const { data, error } = await supabase
      .from("profiles")
      .select("id, full_name, email, expires_at")
      .eq("is_admin", false)
      .not("expires_at", "is", null)
      .gte("expires_at", new Date().toISOString())
      .lte("expires_at", cutoff.toISOString())
      .order("expires_at", { ascending: true });
    if (error) throw error;
    return (data ?? []).map((row) => ({
      id: row.id,
      fullName: row.full_name,
      email: row.email,
      expiresAt: new Date(row.expires_at as string),
    }));
  },
};
